/**
 * Built-in brand mark — a tree: rounded canopy, trunk, two splayed roots, in the
 * two brand golds. It exists so no surface ever renders logo-less. It is the
 * fallback whenever an operator has not uploaded a real logo through the admin
 * branding screen, and the placeholder shared by the login card, the sidebar
 * rail and both app bars.
 *
 * Deliberately inline SVG rather than a file: it costs no request, stays crisp
 * from the 28px app-bar mark up to the 56px login card without a second
 * rendition, and takes its colours from the theme — so it remains visible on
 * both the white and the black canvas with no dark-scheme copy to maintain.
 *
 * Shape note, because it is easy to undo by accident: the canopy is a union of
 * overlapping circles — one bold silhouette. An earlier version fanned thin
 * leaves from a point, which collapsed into an unreadable starburst at the 28–36px
 * sizes that are most of where this renders.
 */
export interface BrandMarkProps {
  /** Rendered edge length in px. The artwork is square. */
  size?: number;
  /** Accessible name; omit for a decorative mark next to a visible wordmark. */
  title?: string;
  className?: string;
}

export function BrandMark({ size = 40, title, className }: BrandMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {title ? <title>{title}</title> : null}

      {/* trunk and roots, under the canopy so the join is hidden */}
      <g fill="none" stroke="var(--fms-color-gold-deep)" strokeLinecap="round" strokeWidth="12">
        <path d="M60 60 V102" />
      </g>
      <g fill="none" stroke="var(--fms-color-gold-deep)" strokeLinecap="round" strokeWidth="8">
        <path d="M60 86 C48 91 40 97 35 107" />
        <path d="M60 86 C72 91 80 97 85 107" />
      </g>

      {/* canopy — same fill and opacity on every circle, so the overlaps union
          into one shape with no visible seams */}
      <g fill="var(--fms-color-gold)">
        <circle cx="60" cy="44" r="32" />
        <circle cx="30" cy="53" r="21" />
        <circle cx="90" cy="53" r="21" />
        <circle cx="43" cy="24" r="18" />
        <circle cx="77" cy="23" r="18" />
      </g>
      {/* one deeper-gold crescent for depth, tracing the canopy's lower edge */}
      <path
        d="M60 76 C36 76 23 61 25 43 C30 59 44 68 60 68 C76 68 90 59 95 43 C97 61 84 76 60 76 Z"
        fill="var(--fms-color-gold-deep)"
        opacity="0.5"
      />
    </svg>
  );
}
